import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { PageSlice, SliceHeroBannerDetails } from '@contracts';
import Colors from '../Colors';
import LazyLoadImage from '../LazyLoadImage';
import LinkButton from '../LinkButton';
import useDesktop from '../useDesktop';
import { TextVariant, Typography } from './Typography';

interface GridProps {
  slice: PageSlice;
}

const HeroBanner: React.FC<GridProps> = ({ slice }) => {
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SliceHeroBannerDetails;
  const isMobile = !useDesktop(sliceDetails?.breakpoint_px || 900);
  const hasImage = isMobile && sliceDetails?.no_mobile_image ? false : !!sliceDetails?.image?.url;

  return (
    <Wrapper>
      <InnerWrap>
        <HeroItem $background={sliceDetails?.background_text} $hasImage={hasImage}>
          {hasImage ? (
            <HeroImageWrap>
              <LazyLoadImage src={sliceDetails.image.url} alt={sliceDetails.image.alt || ''} />
            </HeroImageWrap>
          ) : null}
          <HeroInfoWrap>
            {sliceDetails?.aux_image?.url ? (
              <OverlineImageWrap>
                <LazyLoadImage src={sliceDetails.aux_image.url} alt={sliceDetails.aux_image?.alt || ''} />
              </OverlineImageWrap>
            ) : null}
            {sliceDetails?.overline ? (
              <OverlineWrap>
                <Typography $variant={TextVariant.UpperText4}>{sliceDetails.overline}</Typography>
              </OverlineWrap>
            ) : null}
            {sliceDetails?.heading?.text ? (
              <HeadingWrap>
                <Typography $variant={TextVariant.Heading3} $mobileVariant={TextVariant.Heading5} $breakpoint={1200} as="h4">
                  {sliceDetails.heading.text}
                </Typography>
              </HeadingWrap>
            ) : null}
            {sliceDetails?.bodycopy?.text ? (
              <BodyWrap>
                <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
                  {sliceDetails.bodycopy.text}
                </Typography>
              </BodyWrap>
            ) : null}
            {sliceDetails?.button_url ? (
              <ButtonWrap>
                <LinkButton to={sliceDetails?.button_url}>{t(sliceDetails?.button_label || 'components.learn-more')}</LinkButton>
              </ButtonWrap>
            ) : null}
          </HeroInfoWrap>
        </HeroItem>
      </InnerWrap>
    </Wrapper>
  );
};

export default HeroBanner;

const Wrapper = styled.div`
  width: 100%;
`;

const InnerWrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`;

const HeroItem = styled.div<{ $background?: string; $hasImage?: boolean }>`
  background: ${(props) => props.$background || Colors.babyBlue};
  width: 100%;
  margin: 0 auto;
  display: flex;

  > div {
    width: ${(props) => (props.$hasImage ? '50%' : '100%')};
  }

  @media (max-width: 900px) {
    flex-direction: column;

    > div {
      width: 100%;
    }
  }
`;

const HeroImageWrap = styled.div`
  img {
    display: block;
  }
`;

const HeroInfoWrap = styled.div`
  display: flex;
  padding: 48px 72px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 28px 24px;
  }
`;

const OverlineWrap = styled.div`
  margin-bottom: -8px;
`;

const OverlineImageWrap = styled.div`
  margin-bottom: 8px;
`;

const HeadingWrap = styled.div`
  text-align: center;
`;

const BodyWrap = styled.div`
  text-align: center;
`;

const ButtonWrap = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
`;
